// Countries and plan prices from CMS → E-Paper Settings, for the sign-up
// form and the Renew modal. Same rules as the backend
// (suprabhaatham-cms/src/api/epaper-user/constants/countries.js) and the
// mobile app (app/utils/epaperPricing.ts), so the website shows what the
// server will charge. Every configured currency is offered online as it is;
// PayTabs refusing one is reported by the server as ONLINE_PAYMENT_UNAVAILABLE.
import { useEffect, useState } from "react";
import { API } from "../Config/API";

export type EpaperCountry = {
  id: number;
  name: string;
  code: string;
  currency_code: string;
  dial_code: string;
  default_amount: number;
  is_active: boolean;
  sort_order: number;
};

export type PricedPlan = {
  id: number;
  price: number | string;
  is_active?: boolean;
  status?: string | null;
  sort_order?: number | null;
  country_prices?:
    | Record<string, { currency?: string; price: number | string }>
    | string
    | null;
};

export type PlanPrice = { amount: string | null; currency: string; missing?: boolean };

// Records created before the country field existed are UAE / AED.
export const DEFAULT_COUNTRY = "AE";
export const DEFAULT_CURRENCY = "AED";

// Same wording as the backend and the app.
export const ONLINE_PAYMENT_UNAVAILABLE_MESSAGE =
  "Online payment is not available in your country's currency. " +
  "Please contact a Suprabhaatham agent to complete your subscription.";

const COUNTRY_ALIASES: Record<string, string> = {
  "united arab emirates": "AE",
  uae: "AE",
  ksa: "SA",
};

// Countries offered for new registrations, in the Admin's order.
export const activeCountries = (countries: EpaperCountry[]): EpaperCountry[] =>
  countries
    .filter((c) => c.is_active !== false)
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));

// UAE when it is active, otherwise the first active country.
export const defaultCountry = (countries: EpaperCountry[]): EpaperCountry | undefined => {
  const list = activeCountries(countries);
  return list.find((c) => c.code === DEFAULT_COUNTRY) || list[0];
};

// Configured country (active or not) for a saved code, name or older spelling.
export const findCountry = (
  countries: EpaperCountry[],
  value?: string | null
): EpaperCountry | undefined => {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return undefined;
  const code = COUNTRY_ALIASES[v]?.toLowerCase() || v;
  return countries.find((c) => c.code.toLowerCase() === code || c.name.toLowerCase() === v);
};

const parseCountryPrices = (raw: PricedPlan["country_prices"]) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

// The plan's price for the country: its own country price, free plans stay
// free, AED countries pay the base price, else the country's default amount;
// otherwise `missing` (the plan isn't offered there). Never converted.
export const resolvePlanPrice = (plan: PricedPlan, country: EpaperCountry): PlanPrice => {
  const currency = String(country.currency_code || DEFAULT_CURRENCY).toUpperCase();
  const entry = parseCountryPrices(plan.country_prices)?.[country.code];
  if (entry && entry.price !== undefined && entry.price !== null && entry.price !== "") {
    const price = Number(entry.price);
    if (Number.isFinite(price) && price >= 0) {
      return { amount: String(price), currency: String(entry.currency || currency).toUpperCase() };
    }
  }
  const base = Number(plan.price ?? 0);
  if (!(base > 0)) return { amount: "0", currency };
  if (currency === DEFAULT_CURRENCY) return { amount: String(base), currency };
  const fallback = Number(country.default_amount);
  if (Number.isFinite(fallback) && fallback > 0) return { amount: String(fallback), currency };
  return { amount: null, currency, missing: true };
};

// Active plans priced for the country, in the Admin's order.
export const plansForCountry = <T extends PricedPlan>(plans: T[], country: EpaperCountry): T[] =>
  plans
    .filter(
      (p) =>
        p.is_active !== false &&
        (p.status ?? "active") === "active" &&
        !resolvePlanPrice(p, country).missing
    )
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0) || a.id - b.id);

// E-Paper Settings countries from the public settings route. `countries` is
// null until loaded; `failed` when the request failed.
export function useEpaperCountries(enabled = true) {
  const [countries, setCountries] = useState<EpaperCountry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setFailed(false);
    fetch(`${API.BASE_URL}${API.EPAPER_SETTINGS_CONFIG}`)
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const list = json?.data?.countries;
        if (Array.isArray(list) && list.length) setCountries(list);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { countries, failed };
}
